import * as React from "react";
import { useState } from "react";
import styled, { css } from "styled-components";
import { FormGroup } from "components/bootstrap";
import { InputDescription, Select } from "components/common";
import commonStyles from "components/event-definitions/common/commonStyles.css";
import useLicenseCheck from "license/useLicenseCheck";
import useMitreNameIndex, {
  formatMitreCategory
} from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreNameIndex";
import useMitreFlatOptions from "security-app/components/SecurityEvents/Alerts/MitreCategories/useMitreFlatOptions";
const MAX_TACTICS_TECHNIQUES = 64;
const VALID_PATTERN = /^(TA\d{4}|T\d{4}(\.\d{3})?)$/;
const VALUE_DELIMITER = "";
const quote = (s) => `"${s}"`;
const buildInvalidMessage = (invalidList) => {
  if (invalidList.length === 0) return null;
  const isPlural = invalidList.length > 1;
  return `ID${isPlural ? "s" : ""} ${invalidList.map(quote).join(", ")} ${isPlural ? "are" : "is"} not valid. Expected format: TA0000, T0000, or T0000.000.`;
};
const HELP_TEXT = "Press Enter or Tab to add. Accepts tactic IDs (TA0000), technique IDs (T0000), and sub-technique IDs (T0000.000).";
const HELP_TEXT_WITH_CATALOGUE = "Pick from the installed MITRE catalogue, or type an ID and press Enter or Tab. Accepts tactic IDs (TA0000), technique IDs (T0000), and sub-technique IDs (T0000.000).";
const normalizeId = (raw) => raw.trim().toUpperCase();
const OptionRow = styled.div(
  ({ $depth }) => css`
    display: flex;
    align-items: baseline;
    padding-left: ${$depth * 16}px;
  `
);
const OptionLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ChipLabel = styled.span`
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
`;
const Editor = ({ value, onChange, disabled = false, error = null }) => {
  const [input, setInput] = useState("");
  const [duplicateAttempt, setDuplicateAttempt] = useState(null);
  const { nameById } = useMitreNameIndex();
  const { options } = useMitreFlatOptions();
  const hasCatalogue = options.length > 0;
  const isSearching = input.trim().length > 0;
  const renderOption = (option) => {
    if (option.name === void 0) return /* @__PURE__ */ React.createElement(OptionRow, { $depth: 0 }, option.label);
    const text = `${isSearching ? option.breadcrumb : option.name} (${option.value})`;
    return /* @__PURE__ */ React.createElement(OptionRow, { $depth: isSearching ? 0 : option.depth }, /* @__PURE__ */ React.createElement(OptionLabel, { title: text }, text));
  };
  const renderChip = ({ value: id }) => {
    const label = formatMitreCategory(id, nameById);
    return /* @__PURE__ */ React.createElement(ChipLabel, { title: label }, label);
  };
  const handleChange = (joined) => {
    const raw = joined ? joined.split(VALUE_DELIMITER) : [];
    const normalized = raw.map(normalizeId).filter((v) => v.length > 0);
    const deduped = Array.from(new Set(normalized));
    onChange(deduped.slice(0, MAX_TACTICS_TECHNIQUES));
    setInput("");
    setDuplicateAttempt(null);
  };
  const flagDuplicateAttempt = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    const normalized = normalizeId(trimmed);
    if (value.includes(normalized)) {
      setDuplicateAttempt(normalized);
    }
  };
  const invalidValues = value.filter((v) => !VALID_PATTERN.test(v));
  const selectValue = value.join(VALUE_DELIMITER);
  const isAtMax = value.length >= MAX_TACTICS_TECHNIQUES;
  const selectableOptions = options.map((option) => ({
    ...option,
    disabled: isAtMax || value.includes(option.value)
  }));
  const errorMessages = [
    error ? String(error) : null,
    buildInvalidMessage(invalidValues),
    duplicateAttempt ? `"${duplicateAttempt}" is already added.` : null,
    isAtMax ? `Maximum of ${MAX_TACTICS_TECHNIQUES} entries reached.` : null
  ].filter(Boolean);
  return /* @__PURE__ */ React.createElement(FormGroup, { validationState: errorMessages.length > 0 ? "error" : null }, /* @__PURE__ */ React.createElement(
    Select,
    {
      key: hasCatalogue ? "catalogue" : "bare",
      ...{
        inputValue: input,
        onKeyDown: (e) => {
          if (e.key !== "Enter" && e.key !== "Tab") return;
          const normalized = normalizeId(input);
          if (normalized && value.includes(normalized)) {
            setDuplicateAttempt(normalized);
            e.preventDefault();
            return;
          }
          flagDuplicateAttempt();
        },
        // Offer "Create ..." only for an ID the installed catalogue does not know. While the
        // list still has matches those rows are the answer, and a Create row on a partial
        // word ("Create \"ex\"") is never what the user meant.
        isValidNewOption: (raw) => {
          const query = raw.trim().toLocaleLowerCase();
          return query.length > 0 && !isAtMax && !options.some((o) => o.label.toLocaleLowerCase().includes(query));
        }
      },
      inputId: "tactics-techniques",
      delimiter: VALUE_DELIMITER,
      multi: true,
      allowCreate: true,
      options: selectableOptions,
      optionRenderer: renderOption,
      valueRenderer: renderChip,
      value: selectValue,
      onChange: handleChange,
      onInputChange: (value_, actionMeta) => {
        if ((actionMeta == null ? void 0 : actionMeta.action) === "input-change") {
          setInput(value_);
          setDuplicateAttempt(null);
        }
      },
      onBlur: flagDuplicateAttempt,
      placeholder: "Add tactic/technique IDs (e.g. TA0002, T1059, T1059.001)",
      disabled,
      "aria-label": "Tactics/Techniques"
    }
  ), /* @__PURE__ */ React.createElement(
    InputDescription,
    {
      help: hasCatalogue ? HELP_TEXT_WITH_CATALOGUE : HELP_TEXT,
      error: errorMessages.length > 0 ? errorMessages.join(" ") : void 0
    }
  ));
};
const TacticsTechniquesEditorPlugin = ({ value, onChange, disabled = false, error = null }) => {
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  if (!isValidSecurityLicense) return null;
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, "Tactics/Techniques ", /* @__PURE__ */ React.createElement("small", null, "(optional)")), /* @__PURE__ */ React.createElement("p", null, "Tag this Event Definition with MITRE ATT&CK\xAE tactic, technique, or sub-technique IDs."), /* @__PURE__ */ React.createElement(Editor, { value, onChange, disabled, error }));
};
export default TacticsTechniquesEditorPlugin;
